/**
 * Compatibility layer so `box-ui-elements` (built for react-router v5) works with react-router v6.
 * Webpack resolves `react-router-dom` imports to this module.
 */
import * as React from "react";
import type { PathMatch } from "react-router";
// Local aliases avoid duplicate bindings with `export { … } from "react-router-dom-v6"` below.
// TS 5.x can hit "Debug Failure. False expression" when emitting that pattern without aliases.
import {
  Navigate as RR6Navigate,
  Route as RR6Route,
  matchPath as matchPathRr,
  useLocation as useLocationRr,
  useNavigate as useNavigateRr,
  useParams as useParamsRr,
} from "react-router-dom-v6";

export {
  AbortedDeferredError,
  Await,
  MemoryRouter,
  NavigationType,
  Outlet,
  Router,
  Routes,
  UNSAFE_DataRouterContext,
  UNSAFE_DataRouterStateContext,
  UNSAFE_LocationContext,
  UNSAFE_NavigationContext,
  UNSAFE_RouteContext,
  UNSAFE_useRouteId,
  createMemoryRouter,
  createPath,
  createRoutesFromChildren,
  createRoutesFromElements,
  defer,
  generatePath,
  isRouteErrorResponse,
  json,
  matchPath,
  matchRoutes,
  parsePath,
  redirect,
  redirectDocument,
  renderMatches,
  replace,
  resolvePath,
  Route,
  useActionData,
  useAsyncError,
  useAsyncValue,
  useBlocker,
  useHref,
  useInRouterContext,
  useLoaderData,
  useLocation,
  useMatch,
  useMatches,
  useNavigate,
  useNavigation,
  useNavigationType,
  useOutlet,
  useOutletContext,
  useParams,
  useResolvedPath,
  useRevalidator,
  useRouteError,
  useRouteLoaderData,
  useRoutes,
  UNSAFE_ErrorResponseImpl,
  BrowserRouter,
  Form,
  HashRouter,
  Link,
  NavLink,
  RouterProvider,
  ScrollRestoration,
  UNSAFE_FetchersContext,
  UNSAFE_ViewTransitionContext,
  UNSAFE_useScrollRestoration,
  createBrowserRouter,
  createHashRouter,
  createSearchParams,
  unstable_HistoryRouter,
  unstable_usePrompt,
  useBeforeUnload,
  useFetcher,
  useFetchers,
  useFormAction,
  useLinkClickHandler,
  useSearchParams,
  useSubmit,
  useViewTransitionState,
  Navigate,
} from "react-router-dom-v6";

export type { NavigateFunction } from "react-router-dom-v6";

type HistoryLike = {
  length: number;
  action: string;
  location: ReturnType<typeof useLocationRr>;
  push: (to: string | { pathname?: string; search?: string; hash?: string }) => void;
  replace: (to: string | { pathname?: string; search?: string; hash?: string }) => void;
  go: (n: number) => void;
  goBack: () => void;
  goForward: () => void;
  block: (prompt?: boolean | string | (() => string | boolean)) => () => void;
  listen: () => () => void;
  createHref: (location: { pathname: string; search?: string; hash?: string }) => string;
};

function makeHistory(
  navigate: ReturnType<typeof useNavigateRr>,
  location: ReturnType<typeof useLocationRr>,
): HistoryLike {
  return {
    length: 2,
    action: "POP",
    location,
    push: (to) => navigate(to as string & { pathname?: string }),
    replace: (to) => navigate(to as string & { pathname?: string }, { replace: true }),
    go: (n) => navigate(n),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    block: () => () => {},
    listen: () => () => {},
    createHref: () => "",
  };
}

function toV5Match(m: PathMatch<string>): {
  params: Record<string, string | undefined>;
  isExact: boolean;
  path: string;
  url: string;
} {
  return {
    params: m.params,
    isExact: Boolean(m.pattern.end),
    path: m.pattern.path,
    url: m.pathname,
  };
}

type RouteCompatProps = Omit<React.ComponentProps<typeof RR6Route>, "children"> & {
  children?:
    | React.ReactNode
    | ((props: { match: any; location: ReturnType<typeof useLocationRr>; history: HistoryLike }) => React.ReactNode);
  component?: React.ComponentType<any>;
  render?: (props: { match: any; location: ReturnType<typeof useLocationRr>; history: HistoryLike }) => React.ReactNode;
  computedMatch?: PathMatch<string> | null;
  exact?: boolean;
};

/** v5-style Route (render/component/render-prop children). Box UI Elements imports this as `Route`. */
export function CompatRoute(props: RouteCompatProps): React.ReactElement | null {
  const { path, exact, render, component: Component, children, element, index: isIndex, computedMatch } = props;
  const location = useLocationRr();
  const navigate = useNavigateRr();
  const params = useParamsRr();

  if (element != null || isIndex) {
    return <RR6Route {...(props as React.ComponentProps<typeof RR6Route>)} />;
  }

  const history = makeHistory(navigate, location);

  if (path == null) {
    if (typeof children === "function") {
      const match = {
        path: location.pathname,
        url: location.pathname,
        isExact: true,
        params,
      };
      return <>{children({ match, location, history })}</>;
    }
    return <>{children}</>;
  }

  let matchResult: PathMatch<string> | null | undefined = computedMatch ?? undefined;
  if (!matchResult) {
    const paths = Array.isArray(path) ? path : [path];
    for (const p of paths) {
      if (p == null) continue;
      const m = matchPathRr({ path: p, end: exact !== false }, location.pathname);
      if (m) {
        matchResult = m;
        break;
      }
    }
  }

  if (!matchResult) {
    return null;
  }

  const match = toV5Match(matchResult);

  if (render) {
    return <>{render({ match, location, history })}</>;
  }
  if (Component) {
    return <Component match={match} location={location} history={history} />;
  }
  if (typeof children === "function") {
    return <>{children({ match, location, history })}</>;
  }
  return <>{children}</>;
}

type LegacyRouteChildProps = { path?: string | string[]; exact?: boolean; [key: string]: unknown };

export function Switch({ children }: { children?: React.ReactNode }): React.ReactElement | null {
  const location = useLocationRr();
  const kids = React.Children.toArray(children).filter(Boolean) as React.ReactElement<LegacyRouteChildProps>[];

  const withPath: React.ReactElement<LegacyRouteChildProps>[] = [];
  const withoutPath: React.ReactElement<LegacyRouteChildProps>[] = [];
  for (const child of kids) {
    if (!React.isValidElement(child)) continue;
    if (child.props.path != null) {
      withPath.push(child);
    } else {
      withoutPath.push(child);
    }
  }

  for (const child of withPath) {
    const { path, exact } = child.props;
    const paths = Array.isArray(path) ? path : [path];
    for (const p of paths) {
      if (p == null) continue;
      const m = matchPathRr({ path: p, end: exact !== false }, location.pathname);
      if (m) {
        return React.cloneElement(child, { location, computedMatch: m, key: child.key });
      }
    }
  }

  if (withoutPath.length > 0) {
    const child = withoutPath[0];
    return React.cloneElement(child, { location, key: child.key });
  }

  return null;
}

type RedirectProps = {
  to: string | { pathname?: string; search?: string; hash?: string; state?: unknown };
  push?: boolean;
  from?: string;
};

export function Redirect({ to, push }: RedirectProps): React.ReactElement {
  if (typeof to === "object" && to !== null) {
    const pathname = to.pathname ?? "";
    const search = to.search ?? "";
    const target = `${pathname}${search}`;
    return <RR6Navigate to={target} replace={!push} state={to.state} />;
  }
  return <RR6Navigate to={to} replace={!push} />;
}

export function withRouter<P extends Record<string, unknown>>(
  WrappedComponent: React.ComponentType<
    P & { history: HistoryLike; location: ReturnType<typeof useLocationRr>; match: any }
  >,
): React.ComponentType<P> {
  const C = function (props: P) {
    const renderInjected: RouteCompatProps["children"] = (routeProps: {
      match: any;
      location: ReturnType<typeof useLocationRr>;
      history: HistoryLike;
    }) => <WrappedComponent {...props} {...routeProps} />;
    return <CompatRoute children={renderInjected} />;
  };
  C.displayName = `withRouter(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;
  return C;
}
